'use client';

import { notifyActivationActivity } from '@/features/activation/activity';
import { EventNameType, EventPropertiesType } from '@/utils/analyticsEventTypes';
import { getRudderStack } from '@/lib/rudderstack/rudderstack';
import { getPosthogClient } from '@/lib/posthog/posthog';
import { authStore } from '@/stores/AuthStore';
import { ONBOARDING_MODEL_SPLIT_PROP, TRIAL_BOOKING_FLAG_KEY } from '@poppy/utils';
import { rememberedOnboardingModelSplitSide } from '@/lib/onboardingModelSplit';

/**
 * The single track implementation. Components use it through the
 * useAnalytics hook (which just delegates here); non-component call sites
 * (vault lib operations, handlers) call it directly since hooks can't run
 * outside React. Safe to call anywhere — no-ops on the server, never throws.
 */
export function trackEvent<T extends EventNameType>(
  eventName: T,
  eventProperties: EventPropertiesType<T>
): void {
  if (typeof window === 'undefined') return;
  if (
    // Momentum's observations must never trigger another progress check themselves.
    !(
      eventName === 'feature_milestone' &&
      (eventProperties as { feature_id?: string }).feature_id === 'momentum'
    ) &&
    [
      'Node Created',
      'Nodes Created',
      'Discovery Panel Post Added',
      'vault_item_added',
      'feature_milestone',
    ].includes(eventName)
  )
    notifyActivationActivity();
  try {
    // RudderStack JS SDK automatically handles timestamp, sessionId,
    // messageId, page/device context, library info, etc.

    // Pull PostHog's session ID so events forwarded to PostHog can be used
    // to filter session recordings. RudderStack doesn't add $session_id
    // automatically — only the PostHog Web SDK does.
    const posthog = getPosthogClient();
    const posthogSessionId = posthog?.get_session_id?.();

    // Mirror what posthog-js does for its own captures: stamp the user's active
    // flag variants as `$feature/<key>` on every event. RudderStack-forwarded
    // events don't get this for free, and it's what PostHog funnels, breakdowns
    // and Experiments use to split by variant (see lib/posthog/uiVariants.ts).
    // Server-side events never see this; they join on person properties.
    const featureFlagProps = Object.fromEntries(
      Object.entries(posthog?.featureFlags?.getFlagVariants?.() ?? {}).map(([key, variant]) => [
        `$feature/${key}`,
        variant,
      ])
    );
    // Purchase placement is keyed by Stripe customer ID; posthog-js uses Clerk
    // ID after login and may show a different live flag bucket. The saved
    // purchase placement is the only variant that should reach reports.
    const trialBookingVariant = authStore?.getState().user?.trialBookingAssignment?.variant;
    // The onboarding model split (Sonnet vs Terra) rides on every event of an assigned person the
    // same way, so the per-side money read needs no join (lib/onboardingModelSplit.ts).
    const modelSplitSide = rememberedOnboardingModelSplitSide(authStore?.getState().user?.id);

    getRudderStack().track(eventName, {
      // Only add essential context that RudderStack can't auto-detect
      source: 'web',
      // Vercel previews build with NODE_ENV=production; only VERCEL_ENV tells them apart.
      environment:
        process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ? 'preview' : process.env.NODE_ENV,
      version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',

      ...featureFlagProps,
      ...(trialBookingVariant && {
        [`$feature/${TRIAL_BOOKING_FLAG_KEY}`]: trialBookingVariant,
        trial_booking_variant: trialBookingVariant,
      }),
      ...(modelSplitSide && { [ONBOARDING_MODEL_SPLIT_PROP]: modelSplitSide }),

      // Required by PostHog to associate events with session recordings
      ...(posthogSessionId && { $session_id: posthogSessionId }),

      // User event properties
      ...eventProperties,
    });

    console.log(`📊 Tracked: ${eventName}`, eventProperties);
  } catch (error) {
    console.error(`❌ Failed to track ${eventName}:`, error);
  }
}
